
import Error from "@/components/Error.vue";
import PrettyBar from "@/components/PrettyBar.vue";
import Sidebar from "@/components/Sidebar.vue";
import VDButton from "@/components/VDButton.vue";
import useError from "@/composables/useError";
import useToastService from "@/composables/useToastService";
import {
  RoleType,
  ProfileType,
  LevelOfUse,
  useGetProfileApplicationQuery,
  useGetApplicationQuery,
} from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import gql from "graphql-tag";
import {
  computed,
  defineComponent,
  reactive,
  ref,
  toRef,
  watch,
  inject,
  nextTick,
  onMounted,
} from "vue";
import { onBeforeRouteLeave, useRouter } from "vue-router";
import useConfirmService from "@/composables/useConfirmService";
import { VDPopupListboxClass } from "@/components/pickers/VDPopupListbox";
import RolePicker from "@/views/Profile/components/RolePicker.vue";

//Mutations
import useCreateProfile from "./mutations/useCreateProfile";
import useUpdateProfile from "./mutations/useUpdateProfile";
import useDeleteProfile from "./mutations/useDeleteProfile";
import useAddRoleProfile from "./mutations/useAddRoleProfile";
import useRemoveRoleProfile from "./mutations/useRemoveRoleProfile";

gql`
  query getProfileApplication($id: ID) {
    profile(profileId: $id) {
      ...ProfileParts
      application {
        applicationId
        name
        roles {
          roleId
          name
        }
      }
      roles {
        roleId
        name
      }
    }
  }
`;

export default defineComponent({
  components: {
    Sidebar,
    PrettyBar,
    Error,
    VDButton,
    RolePicker,
  },
  name: "Profile",
  props: {
    applicationId: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const $translate: any = inject("$translate"); //i18n

    // Services
    const toastService = useToastService();
    const router = useRouter();
    const confirmService = useConfirmService("sidebar");

    // VDOM Refs
    const rolePicker = ref<VDPopupListboxClass>();
    const nameInput = ref();

    const originalProfile = ref<ProfileType>({});
    const profile = reactive<ProfileType>({});
    const isNew = computed(() => props.id == "new");
    const hasConfirmedLeave = ref(false);

    const v$ = useVuelidate(useValidations(), {
      name: toRef(profile, "name"),
    });
    const hasChanges = computed(
      () =>
        !(
          originalProfile.value.name == profile.name &&
          originalProfile.value.description == profile.description
        )
    );

    //Call mutations
    const { result, loading, error } = useGetProfileApplicationQuery(
      props,
      () => ({
        enabled: props.id != "new",
        fetchPolicy: "network-only",
      })
    );

    // For new profiles, we need to fetch application data separately
    const { result: applicationResult } = useGetApplicationQuery(
      () => ({ applicationId: props.applicationId }),
      () => ({
        enabled: props.id == "new",
        fetchPolicy: "cache-first",
      })
    );

    watch(
      () => useResult(result).value,
      (newValue) => {
        originalProfile.value = newValue ?? {};
        Object.assign(profile, newValue);
      },
      { immediate: true }
    );

    // Watch for application data when creating new profile
    watch(
      () => useResult(applicationResult).value,
      (applicationData) => {
        if (isNew.value && applicationData) {
          // For new profiles, set the application data
          profile.application = applicationData;
        }
      },
      { immediate: true }
    );

    const createProfileMutation = useCreateProfile();
    const updateProfileMutation = useUpdateProfile();
    const deleteProfileMutation = useDeleteProfile();
    const addRoleProfileMutation = useAddRoleProfile();
    const removeRoleProfileMutation = useRemoveRoleProfile();

    // Functions
    function onPrimaryButton() {
      v$.value.$touch();
      if (!v$.value.$error) {
        isNew.value
          ? createProfileMutation.call(props.applicationId, profile)
          : updateProfileMutation.call(props.applicationId, profile);
      }
    }

    function onAddedRole(role: RoleType) {
      function add() {
        profile.roles = [...(profile.roles ?? []), role];
      }

      if (!isNew.value) {
        if (profile.profileId != undefined && role.roleId != undefined) {
          addRoleProfileMutation
            .call(profile.profileId, role.roleId)
            .then((value) => {
              if (value != null) {
                add();
                toastService.show({
                  severity: "success",
                  title: "Rolle tilføjet",
                  message: "Rollen er blevet tilføjet til rolleprofil",
                });
              }
            });
        }
      } else {
        add();
      }
    }

    function onRemovedRole(role: RoleType) {
      function remove() {
        profile.roles =
          profile.roles?.filter((element) => element?.roleId != role.roleId) ??
          [];
      }

      if (!isNew.value) {
        if (profile.profileId != undefined && role.roleId != undefined) {
          removeRoleProfileMutation
            .call(profile.profileId, role.roleId)
            .then((value) => {
              if (value != null) {
                remove();
                toastService.show({
                  severity: "success",
                  title: "Rolle fjernet",
                  message: "Rollen er blevet fjernet fra rolleprofil",
                });
              }
            });
        }
      } else {
        remove();
      }
    }

    createProfileMutation.onDone(({ data }) => {
      toastService.show({
        title: $translate("TextResources_CreatedProfile"),
        message: `${$translate("TextResources_CreatedProfile")}: ${
          data?.createProfile?.profileId
        }`,
        severity: "success",
      });
      hasConfirmedLeave.value = true;
      router.back();
    });

    updateProfileMutation.onDone(() => {
      toastService.show({
        title: $translate("TextResources_UpdatedProfile"),
        message: $translate("TextResources_UpdatedProfileDescription"),
        severity: "success",
      });
      hasConfirmedLeave.value = true;
      router.back();
    });

    deleteProfileMutation.onDone(() => {
      toastService.show({
        title: $translate("TextResources_DeletedProfile"),
        message: $translate("TextResources_DeletedProfileDescription"),
        severity: "success",
      });
      hasConfirmedLeave.value = true;
      router.back();
    });

    createProfileMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    updateProfileMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    deleteProfileMutation.onError((error) =>
      toastService.show({ severity: "error", error: useError(error) })
    );

    onBeforeRouteLeave(() => {
      if (!hasConfirmedLeave.value && hasChanges.value && !isNew.value) {
        return new Promise((resolve) =>
          confirmService.show(
            null,
            $translate("TextResources_SaveChangeWarningQuestion"),
            $translate("TextResources_SaveChangeWarningAnswerConfirm"),
            $translate("TextResources_SaveChangeWarningAnswerCancel"),
            () => {
              hasConfirmedLeave.value = true;
              resolve(true);
            },
            () => resolve(false)
          )
        );
      } else {
        return true;
      }
    });

    // Focus name input when component is ready
    const focusNameInput = () => {
      nextTick(() => {
        setTimeout(() => {
          // Try multiple ways to access and focus the input
          const inputEl =
            nameInput.value?.$el?.querySelector("input") ||
            nameInput.value?.$el ||
            nameInput.value?.$refs?.input ||
            nameInput.value;

          if (inputEl && typeof inputEl.focus === "function") {
            inputEl.focus();
          }
        }, 100); // Small delay to ensure DOM is ready
      });
    };

    onMounted(() => {
      focusNameInput();
    });

    // Also focus when profile data is loaded for new profiles
    watch(
      () => profile.name,
      (newValue, oldValue) => {
        // Only focus if this is a new profile or initial load
        if (
          isNew.value ||
          ((oldValue === undefined || oldValue === "") && newValue)
        ) {
          focusNameInput();
        }
      }
    );

    return {
      $translate,
      v$,
      isNew,
      nameInput,
      profile,
      loading,
      hasChanges,
      deleteProfileMutation,
      error: useError(error).value || undefined,
      onPrimaryButton,
      onAddedRole,
      onRemovedRole,
      primaryButtonLoading: computed(
        () =>
          createProfileMutation.loading.value ||
          updateProfileMutation.loading.value
      ),
      deleteProfile: (event: Event) => {
        confirmService.show(
          event,
          $translate("TextResources_DeleteProfileWarningQuestion"),
          $translate("TextResources_DeleteProfileWarningnswerConfirm"),
          $translate("TextResources_DeleteProfileWarningAnswerCancel"),
          () => deleteProfileMutation.call(props.applicationId, props.id)
        );
      },
      rolePicker,
      addRemoveLoading: computed(
        () =>
          addRoleProfileMutation.loading.value ||
          removeRoleProfileMutation.loading.value
      ),
    };
  },
});

function useValidations() {
  return {
    name: { required },
  };
}
